/* eslint-disable indent */
/**
 * Implements a DEX info block
 */
import { Network } from '@kattana-io/common';
import Image from '../../../atoms/Image';
import Text from '../../../atoms/Text';
import InfoCoin from '../../../molecules/InfoCoin';
import {
  formatTimestamp,
  toFixed2,
  toFixed3,
} from '../../DexTrades/formatters';
import coingecko from '../../../assets/images/coingecko.svg';
import coinmarketcap from '../../../assets/images/coinmarketcap.svg';
import avaxscan from '../../../assets/images/avaxscan.svg';
import bscscan from '../../../assets/images/bscscan.png';
import bttcscan from '../../../assets/images/bttcscan.svg';
import elrondscan from '../../../assets/images/elrondscan.svg';
import etherscan from '../../../assets/images/etherscan.svg';
import ftmscan from '../../../assets/images/ftmscan.png';
import hecoscan from '../../../assets/images/hecoscan.webp';
import meterscan from '../../../assets/images/meterscan.png';
import tronscan from '../../../assets/images/tronscan.png';
import polygonscan from '../../../assets/images/polygonscan.webp';
import metamask from '../../../assets/images/metamask.svg';
import twitter from '../../../pages/TokenPage/images/twitter.svg';
import telegram from '../../../pages/TokenPage/images/telegram.svg';
import { formatUSD } from '../../Modal/TickersModal/helper';
import CoinInfoField from '../CoinInfoField';

const decorateValue = (key: string, value: string | number): string => {
  switch (key) {
    case 'Daily volume':
      return formatUSD(Math.round(+value));
    case 'Pool created':
      return formatTimestamp(+value);
    case 'Pool liquidity':
      return formatUSD(Math.round(+value));
    case 'APY':
      return `${toFixed2(value)} %`;
    default:
      return value?.toString() ?? '';
  }
};

const addToWallet = async (props: any) => {
  try {
    const wasAdded = await (window as any).ethereum.request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: {
          address: props.base_address, // The address that the token is at.
          symbol: props.slug, // A ticker symbol or shorthand, up to 5 chars.
          decimals: props.base_decimals, // The number of decimals in the token
          image: props.logo_url, // A string url of the token logo
        },
      },
    });

    if (!wasAdded) {
      console.warn('Asset wasnt added due to user rejection');
    }
  } catch (error) {
    console.warn(error);
  }
};

const getScanImage = (network: Network) => {
  if (network.isAVAX()) {
    return avaxscan;
  }
  if (network.isBSC()) {
    return bscscan;
  }
  if (network.isBTTC()) {
    return bttcscan;
  }
  if (network.isELROND()) {
    return elrondscan;
  }
  if (network.isTron()) {
    return tronscan;
  }
  if (network.isFANTOM()) {
    return ftmscan;
  }
  if (network.isHECO()) {
    return hecoscan;
  }
  if (network.isMETER()) {
    return meterscan;
  }
  if (network.isETH()) {
    return etherscan;
  }
  if (network.isPOLYGON()) {
    return polygonscan;
  }
};

const onCopyAddress = (address: string) => {
  navigator.clipboard
    .writeText(address)
    .then(() => {
      console.log('Copied to clipboard');
    })
    .catch((err) => {
      console.warn('Could not copy to clipboard', err);
    });
};

interface IDexCoinInfoProps {
  exchange: string;
  name: string;
  base: string;
  quote: string;
  chain: string;
  base_address: string;
  address: string;
  quote_address: string;
  base_precision: number;
  base_decimals: number;
  quote_decimals: number;
  loading: boolean;
  flipped: boolean;
  new: boolean;
  id: number;
  hidden: boolean;
  hot: boolean;
  rank: number;
  kind: number;
  pool_created: number;
  base_id: number;
  quote_id: number;
  price: string;
  price_a: number;
  price_a_usd: number;
  price_b: number;
  price_b_usd: number;
  liquidity: number;
  volume: number;
  base_reserve: number;
  quote_reserve: number;
  updated_at: number;
  annual_percentage_yield: number;
  base_slug: string;
  quote_slug: string;
  title: string;
  slug: string;
  coingecko_id: string;
  coinmarketcap_id: string;
  trustwallet_id: string;
  logo_url: string;
  website: string;
  long_title: string;
  telegram: string;
  twitter_id: string;
  reddit: string;
  github: string;
  priceChange: number;
  priceChangePercent: number;
  info: any;
  network: any;
}
const DexCoinInfo = (props: IDexCoinInfoProps & any) => {
  const address = props.flipped ? props.quote_address : props.base_address;

  const scanImage = getScanImage(props.network);

  return (
    <>
      <div className='info_coin__mt'>
        <InfoCoin
          {...props}
          address={address}
          onCopyAddress={() => onCopyAddress(address)}
        />
      </div>
      <div className='row'>
        <div className='row'>
          {props.network && address && (
            <div
              className='icon_link_img'
              onClick={() =>
                window.open(props.network.getScanForToken(address))
              }
            >
              {scanImage ? (
                <Image src={scanImage} size='s' />
              ) : (
                <div
                  title='Scan for token'
                  className='icon_link_img__not_found icon-search-alt'
                />
              )}
            </div>
          )}
          {props.coingecko_id && (
            <a
              href={`https://www.coingecko.com/en/coins/${props.coingecko_id}`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={coingecko} size='s' />
            </a>
          )}
          {props.coinmarketcap_id && (
            <a
              href={`https://coinmarketcap.com/currencies/${props.coinmarketcap_id}/`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={coinmarketcap} size='s' />
            </a>
          )}
          {props.telegram && (
            <a
              href={`https://t.me/${props.telegram}/`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={telegram} size='s' />
            </a>
          )}
          {props.twitter_id && (
            <a
              href={`https://twitter.com/${props.twitter_id}/`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={twitter} size='s' />
            </a>
          )}
          {!props.network?.isTron() && (
            <div className='icon_link_img' onClick={() => addToWallet(props)}>
              <Image src={metamask} size='s' />
            </div>
          )}
        </div>
        <div
          className={`fragment info_coin__price ${
            props.priceChangePercent > 0 ? 'text--green' : 'text--red'
          }`}
        >
          <Text size={18} style={{ title: props.price }}>
            {props.price}
          </Text>
          <Text size={12} className='info_coin__price__change'>
            ({toFixed2(props.priceChangePercent)}%) $
            {toFixed2(props.priceChange)}
          </Text>
        </div>
      </div>
      {Object.keys(props.info).map((key) => (
        <CoinInfoField
          key={key}
          name={key}
          value={decorateValue(key, props.info[key])}
        />
      ))}
    </>
  );
};

export default DexCoinInfo;
